/**
 * An ascent's photo (docs/archive/plans/2026-10-05-ascent-photos-share-design.md): prepared on the device (long
 * edge PHOTO_LONG_EDGE, re-encoded so EXIF and GPS stay behind), stored in R2 as uploaded, and served to
 * anyone but its owner only as the widths the server re-encoded: a modified client cannot leak metadata.
 */

/** Photos an ascent can have. The API speaks arrays, so more is this constant and a gallery. */
export const MAX_ASCENT_PHOTOS = 1;
/** The long edge a photo is prepared at on the device. */
export const PHOTO_LONG_EDGE = 2048;
/** What the server takes: the upload's bytes, and each side in pixels. */
export const PHOTO_MAX_BYTES = 8 * 1024 * 1024;
export const PHOTO_MIN_PX = 320;
export const PHOTO_MAX_PX = 4096;
/** Photos a user may store in a rolling day (a replaced one frees its place: storage, not requests). */
export const PHOTO_UPLOADS_PER_DAY = 60;
/** Widths the server derives as WebP: rows (56 pt at 3x) and cards. Others only ever get these. */
export const PHOTO_WIDTHS = [320, 1080] as const;

export type PhotoWidth = (typeof PHOTO_WIDTHS)[number];
export type PhotoSize = PhotoWidth | "full";

/** A photo of an ascent: its client id and its size as uploaded. `hidden`: after reports (its owner's view only). */
export type AscentPhoto = { id: string; w: number; h: number; hidden?: boolean };

/** The original in R2. */
export const photoKey = (userId: string, ascentId: string, photoId: string, ext: string) => `p/${userId}/${ascentId}/${photoId}.${ext}`;
/** A derived width in R2. */
export const photoWidthKey = (userId: string, ascentId: string, photoId: string, width: PhotoWidth) => `p/${userId}/${ascentId}/${photoId}/${width}.webp`;
/** Everything one photo has in R2: the original and its widths. */
export const photoKeys = (originalKey: string) => {
  const base = originalKey.replace(/\.[a-z]+$/, "");
  return [originalKey, ...PHOTO_WIDTHS.map((w) => `${base}/${w}.webp`)];
};

/** Where the app reads a photo. The URL changes with every upload (a new id), so it is cached for good. */
export const ascentPhotoUrl = (userId: string, ascentId: string, photoId: string, size: PhotoSize) =>
  `/api/ascent-photos/${userId}/${ascentId}/${photoId}/${size}`;

/** The size to request for a box `cssWidth` wide at the screen's density. */
export const photoWidthFor = (cssWidth: number, dpr = 3): PhotoWidth => (cssWidth * dpr <= PHOTO_WIDTHS[0] * 1.25 ? PHOTO_WIDTHS[0] : PHOTO_WIDTHS[1]);
